import React, { useEffect, useState, useCallback } from "react";
import { Modal, Button, Form, Table, Container, InputGroup, Badge, Row, Col } from "react-bootstrap";
import { FiSearch, FiEdit2, FiPlus } from "react-icons/fi";
import { createBank, getAllBanks, updateBank } from "../../../api/onboarddeboard";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Bank = () => {
  const token = localStorage.getItem("token");

  const [banks, setBanks] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);

  const [form, setForm] = useState({
    bankName: "",
    branch: "",
    status: "Active",
  });
  const [errors, setErrors] = useState({});

  const fetchBanks = useCallback(async () => {
    try {
      const res = await getAllBanks(token);
      if (res.status === 200) setBanks(res.data);
    } catch (err) {
      console.error(err);
    }
  }, [token]);

  useEffect(() => {
    fetchBanks();
  }, [fetchBanks]);

  const handleOpenAddModal = () => {
    setEditId(null);
setForm({ bankName: "", branch: "", status: "Active" });
    setErrors({});
    setShowModal(true);
  };

  const handleEdit = (bank) => {
    setEditId(bank.id);
    setForm({
      bankName: bank.bankName || "",
      branch: bank.branch || "",
      status: bank.status || "Active",
    });
    setErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setErrors({});
    setEditId(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleToggleStatus = () => {
    setForm((prev) => ({
      ...prev,
      status: prev.status === "Active" ? "Inactive" : "Active",
    }));
  };

  const validate = () => {
    const e = {};
   const trimmedName = form.bankName.trim();

    if (!trimmedName) {
      e.bankName = "Bank Name is required";
    } else if (/^[0-9]+$/.test(trimmedName)) {
      e.bankName = "Bank Name cannot be numbers only";
    } else if (/^[^a-zA-Z0-9]+$/.test(trimmedName)) {
      e.bankName = "Bank Name cannot contain only special characters";
    } else if (!/^[A-Za-z]+(?:\s[A-Za-z]+)*$/.test(trimmedName)) {
      e.bankName = "Bank Name must contain only alphabets";
    }

    return e;
  };

  const handleSubmit = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const payload = {
      bankName: form.bankName.trim(),
      branch: form.branch.trim(),
      status: form.status,
    };

    if (editId) {
      const existing = banks.find((b) => b.id === editId);
      const noChange =
        existing &&
        existing.bankName?.trim() === payload.bankName &&
        (existing.branch?.trim() || "") === payload.branch &&
        existing.status === payload.status;
      if (noChange) {
        toast.warn("No changes found for updation");
        return;
      }
    }

    try {
      if (editId) {
        await updateBank(editId, payload, token);
        toast.success("Bank updated successfully");
      } else {
        await createBank(payload, token);
        toast.success("Bank created successfully");
      }
      handleCloseModal();
      fetchBanks();
    } catch (err) {
      toast.error("Something went wrong");
    }
  };

  const filtered = banks.filter((b) =>
    b.bankName?.toLowerCase().includes(searchText.toLowerCase())
  );

  const maskAccount = (num) => {
    if (!num) return "—";
    const str = String(num);
    return "****" + str.slice(-4);
  };

  return (
    <Container fluid className="p-4">

      {/* Header */}
      <div className="d-flex justify-content-between align-items-start mb-2 flex-wrap gap-2">
        <div>
          <h5 className="page-main-heading">Bank</h5>
          <small className="page-sub-heading">Manage company bank information for payroll and transactions</small>
        </div>
        {/* Add Bank button — color #2E5CB8 */}
        <Button
          onClick={handleOpenAddModal}
          style={{ borderRadius: "8px", fontWeight: 500, background: "#2E5CB8", border: "none" }}
        >
          <FiPlus className="me-1" /> Add Bank
        </Button>
      </div>

      {/* Search */}
      <div className="mb-3 mt-3" style={{ maxWidth: 340 }}>
        <InputGroup>
          <InputGroup.Text style={{ background: "#fff" }}>
            <FiSearch color="#888" />
          </InputGroup.Text>
          <Form.Control
            placeholder="Search banks"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ borderLeft: "none" }}
          />
        </InputGroup>
      </div>

      {/* Table */}
      <div style={{ background: "#fff", borderRadius: 10, boxShadow: "0 1px 4px rgba(0,0,0,0.07)", overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <Table hover responsive className="mb-0 align-middle" style={{ minWidth: 650 }}>
            <thead>
              <tr style={{ background: "#f8f9fa", borderBottom: "2px solid #e9ecef" }}>
                <th style={{ padding: "14px 20px", fontWeight: 600, color: "#444" }}>Bank Name</th>
                <th style={{ padding: "14px 20px", fontWeight: 600, color: "#444", textAlign: "center" }}>Status</th>
                <th style={{ padding: "14px 20px", fontWeight: 600, color: "#444", textAlign: "center" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
<td colSpan="3" className="text-center py-5 text-muted">No banks found</td>                </tr>
              ) : (
                filtered.map((bank) => (
                  <tr key={bank.id}>
                    <td style={{ padding: "14px 20px", fontWeight: 500 }}>{bank.bankName}</td>
                    <td style={{ padding: "14px 20px", textAlign: "center" }}>
                      <Badge
                        bg={(bank.status || "Active") === "Active" ? "success" : "secondary"}
                        style={{ fontSize: "0.78rem", padding: "5px 12px", borderRadius: 20 }}
                      >
                        {bank.status || "Active"}
                      </Badge>
                    </td>
                    {/* Delete button removed — only Edit remains */}
                    <td style={{ padding: "14px 20px", textAlign: "center" }}>
                      <button
                        onClick={() => handleEdit(bank)}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#4466ee" }}
                        title="Edit"
                      >
                        <FiEdit2 size={17} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      </div>

      {/* Add / Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered size="md">
        <Modal.Header closeButton style={{ borderBottom: "1px solid #eee" }}>
          <Modal.Title style={{ fontSize: "1.1rem", fontWeight: 600 }}>
            {editId ? "Edit Bank" : "Add Bank"}
          </Modal.Title>
        </Modal.Header>
        <p className="text-muted px-4 pt-2" style={{ fontSize: 12, marginBottom: 0 }}>
          {editId ? "Update the bank details." : "Add a new bank for company payroll and transactions."}
        </p>

        <Modal.Body className="p-4">

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold" style={{ fontSize: 13 }}>
              Bank Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="bankName"
              placeholder="e.g. HDFC Bank"
              value={form.bankName}
              onChange={handleChange}
              style={{ borderRadius: 8, height: 42 }}
              isInvalid={!!errors.bankName}
              autoFocus
            />
            {errors.bankName && <small className="text-danger">{errors.bankName}</small>}
          </Form.Group>

         <Form.Group className="mb-3">
            <Form.Label className="fw-semibold" style={{ fontSize: 13 }}>
              Branch (Optional)
            </Form.Label>
           <Form.Control
                type="text"
                name="branch"
                placeholder="e.g. MG Road"
                value={form.branch}
              onChange={handleChange}
              style={{ borderRadius: 8, height: 42 }}
            />
          </Form.Group>

          {/* Active Status toggle — green when Active */}
          <Form.Group className="mb-2">
            <div className="d-flex justify-content-between align-items-center">
              <Form.Label className="fw-semibold mb-0" style={{ fontSize: 13 }}>
                Active Status
              </Form.Label>
              <div
                onClick={handleToggleStatus}
                style={{
                  width: 34,
                  height: 18,
                  borderRadius: 50,
                  background: form.status === "Active" ? "#22c55e" : "#ccc",
                  cursor: "pointer",
                  position: "relative",
                  transition: "background 0.2s",
                  flexShrink: 0,
                }}
              >
                <div style={{
                  position: "absolute",
                  top: 2,
                  left: form.status === "Active" ? 18 : 2,
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  background: "#fff",
                  transition: "left 0.2s",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                }} />
              </div>
            </div>
          </Form.Group>

        </Modal.Body>

        <Modal.Footer style={{ borderTop: "1px solid #eee" }}>
          <Button variant="secondary" onClick={handleCloseModal} style={{ borderRadius: 7, minWidth: 90 }}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} style={{ borderRadius: 7, minWidth: 90, background: "#2E5CB8", border: "none" }}>
            {editId ? "Update" : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer />
    </Container>
  );
};

export default Bank;